import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

export interface DeviceInfo {
  userAgent: string;
  platform: string;
  language: string;
  screenResolution: string;
  viewportSize: string;
  pixelRatio: number;
  timeZone: string;
}

export interface ClientLogEntry {
  module: 'FRONTEND' | 'MOBILE';
  level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG';
  message: string;
  requestId: string;
  url?: string;
  method?: string;
  status?: number;
  durationMs?: number;
  requestBody?: any;
  responseBody?: any;
  deviceInfo?: DeviceInfo;
  context?: Record<string, any>;
  timestamp: string;
  stackTrace?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private router = inject(Router, { optional: true });

  private logQueue: ClientLogEntry[] = [];
  private flushTimer: any = null;
  private readonly FLUSH_INTERVAL_MS = 2000;
  private readonly MAX_BATCH_SIZE = 25;
  private readonly LOG_ENDPOINT = '/api/logs/frontend';

  constructor() {
    // Flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flushLogsNow());
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flushLogsNow();
        }
      });
    }
  }

  /**
   * Generates a unique correlation Request ID for tracing.
   */
  public generateRequestId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return 'req-fe-' + crypto.randomUUID();
    }
    return 'req-fe-' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
  }

  /**
   * Logs an event and schedules ingestion.
   */
  public log(entry: Partial<ClientLogEntry> & { message: string; level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG' }): void {
    const fullEntry: ClientLogEntry = {
      module: 'FRONTEND',
      level: entry.level || 'INFO',
      message: entry.message,
      requestId: entry.requestId || this.generateRequestId(),
      url: entry.url || this.getCurrentRoute(),
      method: entry.method,
      status: entry.status,
      durationMs: entry.durationMs,
      requestBody: this.maskData(entry.requestBody),
      responseBody: this.maskData(entry.responseBody),
      deviceInfo: this.getDeviceInfo(),
      context: {
        currentRoute: this.getCurrentRoute(),
        ...(entry.context || {})
      },
      timestamp: new Date().toISOString(),
      stackTrace: entry.stackTrace
    };

    // Console output for local browser debugging
    this.printToConsole(fullEntry);
  }

  public info(message: string, context?: Record<string, any>, requestId?: string): void {
    this.log({ level: 'INFO', message, context, requestId });
  }

  public warn(message: string, context?: Record<string, any>, requestId?: string): void {
    this.log({ level: 'WARN', message, context, requestId });
  }

  public error(message: string, stackTrace?: string, context?: Record<string, any>, requestId?: string): void {
    this.log({ level: 'ERROR', message, stackTrace, context, requestId });
  }

  /**
   * Flushes queued logs (no-op as server file logging is disabled).
   */
  public flushLogsNow(): void {
    this.logQueue = [];
  }

  private getCurrentRoute(): string {
    return this.router?.url || (typeof window !== 'undefined' ? window.location.pathname : '');
  }

  public getDeviceInfo(): DeviceInfo {
    if (typeof window === 'undefined') {
      return {
        userAgent: 'SSR',
        platform: 'Server',
        language: 'en',
        screenResolution: '0x0',
        viewportSize: '0x0',
        pixelRatio: 1,
        timeZone: 'UTC'
      };
    }

    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform || 'Unknown',
      language: navigator.language || 'en',
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      pixelRatio: window.devicePixelRatio || 1,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    };
  }

  /**
   * Mask sensitive keys (password, token, otp, pin, secret, card) recursively.
   */
  public maskData(data: any): any {
    if (!data) return data;

    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        return JSON.stringify(this.maskObject(parsed));
      } catch {
        return data;
      }
    }

    if (typeof data === 'object') {
      return this.maskObject(data);
    }

    return data;
  }

  private maskObject(obj: any): any {
    if (!obj || typeof obj !== 'object') return obj;

    if (Array.isArray(obj)) {
      return obj.map(item => this.maskObject(item));
    }

    const sensitiveRegex = /^(pass(word)?|pwd|currentPassword|newPassword|confirmPassword|accessToken|refreshToken|token|jwt|secret|otp|pin|cvv|cardNumber|creditCard)$/i;

    const result: Record<string, any> = {};
    for (const key of Object.keys(obj)) {
      if (sensitiveRegex.test(key)) {
        result[key] = '***MASKED***';
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        result[key] = this.maskObject(obj[key]);
      } else {
        result[key] = obj[key];
      }
    }
    return result;
  }

  private printToConsole(entry: ClientLogEntry): void {
    const formatted = `[${entry.timestamp}] [FRONTEND] [${entry.level}] [${entry.requestId}] ${entry.message}`;
    switch (entry.level) {
      case 'ERROR':
        console.error(formatted, entry.context || '', entry.stackTrace || '');
        break;
      case 'WARN':
        console.warn(formatted, entry.context || '');
        break;
      default:
        console.log(formatted, entry.context || '');
        break;
    }
  }
}
